import {
  useGetFailedLeaveJobsQuery,
  useGetFailedLeaveRecordsQuery,
  useLazyDownloadLeaveTemplateQuery,
  useUploadLeaveMutation,
} from '@/app/features/employee/emp-leave/emp-leave.api';
import type { FailedLeaveRecord } from '@/app/features/employee/emp-leave/emp-leave.type';
import { BulkUploader, BulkUploadErrors } from '@/components/BulkUploader';
import useModal from '@/hooks/useModal';
import { toast } from '@/lib/toast';
import { Button, type TableProps } from 'antd';

type Props = {
  open: boolean;
  handleClose: (value?: boolean) => void;
};

const columns: TableProps<FailedLeaveRecord>['columns'] = [
  {
    title: 'Row',
    dataIndex: 'row_number',
    width: 80,
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Email',
    dataIndex: 'email',
    render: (val) =>
      val ? <span className="block min-w-[120px]">{val}</span> : 'n/a',
  },
  {
    title: 'Leave Type',
    dataIndex: 'leave_type_name',
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Start Date',
    dataIndex: 'start_date',
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'End Date',
    dataIndex: 'end_date',
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Failure Reason',
    dataIndex: 'failure_reason',
    render: (val) =>
      val ? (
        <span className="block min-w-[200px] text-red-400">{val}</span>
      ) : (
        'n/a'
      ),
  },
];

export default function BulkLeaveUpload({ open, handleClose }: Props) {
  const [uploadLeave, { isLoading: isUploading }] = useUploadLeaveMutation();
  const [downloadTemplate] = useLazyDownloadLeaveTemplateQuery();

  const {
    open: errorOpen,
    handleOpen: errorHandleOpen,
    handleClose: errorClose,
  } = useModal<undefined, 'ERROR_SHOW'>();

  const handleDownloadTemplate = async () => {
    try {
      const blobUrl = await downloadTemplate().unwrap();
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'leave-template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast('error', 'Failed to download template.');
    }
  };

  const handleUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    await uploadLeave(formData).unwrap();
  };

  return (
    <>
      <BulkUploader
        open={open}
        onClose={() => handleClose()}
        title="Bulk Leave Upload"
        subtitle="File Type Must be in .xlsx"
        accept=".xlsx"
        onDownloadTemplate={handleDownloadTemplate}
        onUpload={handleUpload}
        uploadLoading={isUploading}
        errorViewer={
          <Button
            type="default"
            className="!h-10 text-center text-teal-500 text-sm font-normal underline !bg-transparent !border-none !shadow-none"
            onClick={() => errorHandleOpen({ type: 'ERROR_SHOW' })}
          >
            View Error Table Data
          </Button>
        }
      />

      <BulkUploadErrors<FailedLeaveRecord>
        open={errorOpen.open}
        onClose={errorClose}
        useGetJobsQuery={useGetFailedLeaveJobsQuery}
        useGetRecordsQuery={useGetFailedLeaveRecordsQuery}
        columns={columns}
        jobsErrorMessage="Failed to get leave import error data"
        recordsErrorMessage="Failed to get leave import error data"
      />
    </>
  );
}
